import {
    Badge,
    classNames,
    EmailKeyboardLayout,
    IconNames,
    Keyboard,
    layout,
    MaterialIcon,
    PushButton,
    TextInput,
    theme,
    usePlatform, useStorageItemValue,
    useTypeRef,
    View
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {stylesheet} from "typestyle";
import {useSendBookingCode} from "./verify";
import {useLocalization} from "../../i18n";

interface EmailFormProps {
    onSent: (data: {email: string}) => any;
    onBack: () => any;
    code: string;
}

export function BookingCodeEmail({onSent, onBack, code}: EmailFormProps) {
    const {t} = useLocalization();
    const [email, setEmail] = useState("");
    const input = useRef<HTMLInputElement|null>();
    const [type, setType] = useTypeRef();

    useEffect(() => {
        if (input.current) {
            input.current.focus();
        }
    }, [input.current]);

    const platform = usePlatform();
    const topEmailDomains = (useStorageItemValue<string[]>("topEmailDomains") ?? [])
		.map(d => "@" + d);

    const send = useSendBookingCode(platform, code, "verifyEmailUrl", onSent);

    const handleSubmit = useCallback(() => send.submit({email}), [send.submit, email]);

    return (
        <View className={classNames(layout.vertical, layout.spaceEvenly)}>
            <div className={classNames(layout.paddingMedium)}>
                <div className={styles.title}>
                    {t("BookingEnterEmail")}
                </div>
                <div>
                    {t("BookingEnterEmailDescription")}
                </div>
            </div>

            <div className={classNames(theme.moreContrast)}>
                <TextInput
                    onChange={setEmail}
                    disabled={send.loading}
                    value={email}
                    placeholder={t("BookingEmailAddressPlaceholder")}
                    inputRef={input}
                    typeRef={setType}
                    className={classNames(layout.fill, styles.textInput)}
                />
                <Keyboard
                    className={classNames(styles.keyboard)}
                    onType={type}
                    border={false}
                    layout={EmailKeyboardLayout}
                    sideKeys={topEmailDomains}
                />
            </div>

            <div className={classNames(layout.paddingMedium)}>
                <div className={classNames(layout.horizontal)}>
                    <div>
                        <PushButton onClick={onBack}>{t("Back")}</PushButton>
                    </div>
                    <div className={classNames(layout.fill, layout.paddingSmall)}>
                        {send.invalid && (
                            <Badge error>
                                {t("InvalidEmail")}
                            </Badge>
                        )}
                        {send.failed && (
                            <Badge error>
                                {t("SendVerificationFailedInternal")}
                            </Badge>
                        )}
                    </div>
                    <div>
                        <PushButton primary onClick={handleSubmit} icon={<MaterialIcon icon={IconNames.send}/>}>
                            {t("Submit")}
                        </PushButton>
                    </div>
                </div>
            </div>

        </View>
    );
}

const styles = stylesheet({
    title: {
        fontSize: "1.75rem",
    },
    textInput: {
        fontWeight: "bold",
        width: "100%",
        height: "4rem",
        fontSize: "2em",
    },
    keyboard: {
        backgroundColor: "transparent",
        fontSize: "1.25rem",
    },
})
